import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';

import { Box, Button, CircularProgress, Container, CssBaseline, TextField, Typography } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Backdrop from '@mui/material/Backdrop';

const theme = createTheme();

const PasswordReset = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [isTokenValid, setIsTokenValid] = useState(false);
  const [loading, setLoading] = useState(true);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm({ mode: 'all' });

  const password = useRef({});
  password.current = watch('password');

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const res = await axios.post('/api/auth/verify-reset-token', { token });
        if (res.data?.valid) {
          setIsTokenValid(true);
        } else {
          setIsTokenValid(false);
        }
      } catch (err) {
        setIsTokenValid(false);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setIsTokenValid(false);
      setLoading(false);
    }
  }, [token]);

  const onSubmit = async (data) => {
    try {
      await axios.patch('/api/user/resetPassword', {
        token,
        newPassword: data.password
      });
      alert('비밀번호가 성공적으로 변경되었습니다.');
      navigate('/login');
    } catch (err) {
      alert('비밀번호 변경 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return (
      <Backdrop open>
        <CircularProgress />
      </Backdrop>
    );
  }

  if (!isTokenValid) {
    return (
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="h6">🔒 링크가 만료되었거나 잘못되었습니다.</Typography>
          <Typography variant="body2">다시 요청해 주세요.</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
            비밀번호 재설정
          </Typography>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 1 }}>
            <TextField
              margin="dense"
              fullWidth
              required
              label="새 비밀번호"
              type="password"
              {...register('password', {
                required: '비밀번호를 입력해주세요.',
                pattern: {
                  value: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d~!@#$%^&*()+|=]{8,20}$/,
                  message: '문자, 숫자 포함 8~20자로 입력해주세요.'
                }
              })}
              error={!!errors.password}
              helperText={errors.password?.message}
            />
            <TextField
              margin="dense"
              fullWidth
              required
              label="비밀번호 확인"
              type="password"
              {...register('passwordCheck', {
                required: '비밀번호 확인을 입력해주세요.',
                validate: (value) => value === password.current || '비밀번호가 일치하지 않습니다.'
              })}
              error={!!errors.passwordCheck}
              helperText={errors.passwordCheck?.message}
            />
            <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
              비밀번호 변경
            </Button>
          </Box>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default PasswordReset;
